/**
 * The single source of contract truth for `/api/v1`.
 *
 * Both the Hono routes and the browser client import these schemas; a response
 * shape can therefore only change in one place. Requests are parsed on the server,
 * responses are typed on the client.
 */
import { z } from "zod";
import {
  ACCESS_ROLES, ATTENDANCE_SOURCES, ATTENDANCE_STATUSES, BLOOD_GROUPS, CLAIM_STATES,
  EFFECTIVE_ROLES, EMERGENCY_RELATIONSHIPS, EMPLOYEE_STATUSES, EMPLOYMENT_TYPES,
  HOLIDAY_KINDS, LEAVE_DURATIONS, LEAVE_STATUSES, LEVELS, SENSITIVE_FIELDS,
  isCalendarDate, isContactNumber,
} from "./domain.js";

/* ------------------------------ primitives ------------------------------ */

export const zDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
/**
 * A date that is also a day on the calendar.
 *
 * `zDate` only asserts the shape, which is the right bound for a filter — an
 * impossible date simply matches nothing. It is the wrong bound for a date being
 * *written*: `2026-02-30` reaches Postgres and comes back as `date/time field
 * value out of range`, a database sentence on a form field.
 */
export const zRealDate = zDate.refine(isCalendarDate, "that day is not on the calendar");
export const zMonth = z.string().regex(/^\d{4}-\d{2}$/, "expected YYYY-MM");
/** A four-digit calendar year, as the holiday calendar and its readers accept it. */
export const zYear = z.coerce.number().int().min(1970).max(9999);
/**
 * A `BP-nnnn` employee code.
 *
 * The pattern still accepts three digits because the codes that predate migration
 * 0014 are three digits wide and are referenced from `org_settings`, exports and
 * anything anyone has ever pasted. The *message* is what a person reads when they
 * type one wrong, and it names the width every code in the roster now has.
 */
export const zEmployeeCode = z.string().regex(/^BP-\d{3,}$/, "expected an employee code like BP-0007");
export const zUuid = z.string().uuid();
/** An employee is addressable by uuid or by its stable BP-nnn business code. */
export const zEmployeeRef = z.union([zUuid, zEmployeeCode]);
export const zMoney = z.string().regex(/^-?\d+(\.\d{1,2})?$/, "expected a decimal amount");

/**
 * A department name.
 *
 * Departments are rows in `departments`, not a compile-time list, so the contract
 * can only bound the shape — the database is the authority on which names exist.
 * These parameters used to be `z.enum(DEPTS)`, which made the seven prototype
 * names the only ones the API would accept: four of them matched no row, and a
 * request for the real "Leadership" (the CEO's department) was rejected with a 422
 * before any handler ran. As a *filter* an unknown name is not an error at all —
 * it selects nothing, exactly like an unknown search term. As a *destination* it
 * is, and `resolveDepartmentId` on the server answers with a field-level 422.
 */
export const zDepartmentName = z.string().trim().min(1).max(80);

/* -------------------------------- errors -------------------------------- */

export const ERROR_CODES = [
  "unauthenticated", "forbidden", "not_found", "validation_failed", "conflict",
  "state_conflict", "rate_limited", "storage_unavailable", "database_unavailable",
  "payload_too_large", "unsupported_media_type", "internal_error",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    requestId: z.string(),
    fieldErrors: z.record(z.string(), z.array(z.string())).optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/* --------------------------- session & identity -------------------------- */

export const CapabilitiesSchema = z.object({
  canViewAllEmployees: z.boolean(),
  canManageEmployees: z.boolean(),
  canExitEmployees: z.boolean(),
  /**
   * May the viewer directly correct an employment record — department, reporting
   * line, work email, city — from the employee drawer.
   *
   * Super Admin only, and deliberately narrower than `canManageEmployees` (which HR
   * Admins also hold). It edits the *structural* facts a person sits inside — the
   * ones that reshape the org chart and re-route leave approvals — rather than
   * appending to their history the way a designation change does, so it is held to
   * the highest tier. Band and title still change through `changeDesignation`, which
   * keeps the append-only employment history intact.
   */
  canEditEmployment: z.boolean(),
  canAdministerLeave: z.boolean(),
  canViewAttendanceAll: z.boolean(),
  canAdministerClaims: z.boolean(),
  canPayClaims: z.boolean(),
  canViewAllReviews: z.boolean(),
  canViewAllNotes: z.boolean(),
  canAddNoteAnyone: z.boolean(),
  canViewReports: z.boolean(),
  canManageSettings: z.boolean(),
  /**
   * May the viewer change anybody's access role (Settings → Access & roles).
   * Super Admin only: an HR Admin who could hand out Super Admin could reach every
   * power held above them. HR Admins still read the list.
   */
  canManageAccessRoles: z.boolean(),
  canManageResources: z.boolean(),
  canDownloadResources: z.boolean(),
  canRunAudit: z.boolean(),
});
export type Capabilities = z.infer<typeof CapabilitiesSchema>;

export const MeSchema = z.object({
  employee: z.object({
    id: zUuid,
    code: zEmployeeCode,
    name: z.string(),
    role: z.string(),
    level: z.enum(LEVELS),
    department: z.string(),
    workEmail: z.string(),
    status: z.enum(EMPLOYEE_STATUSES),
    type: z.enum(EMPLOYMENT_TYPES),
    doj: zDate,
    /** Your own photo, for the sidebar. Null means the identicon stands. */
    photoUrl: z.string().nullable().optional(),
  }),
  effectiveRole: z.enum(EFFECTIVE_ROLES),
  accessRole: z.enum(ACCESS_ROLES),
  workspace: z.enum(["admin", "manager", "employee"]),
  hasReports: z.boolean(),
  capabilities: CapabilitiesSchema,
  onboardingComplete: z.boolean(),
  csrfToken: z.string(),
  serverTimeIst: z.string(),
});
export type Me = z.infer<typeof MeSchema>;

export const CreateSessionRequest = z.object({ idToken: z.string().min(1) });

export const HealthSchema = z.object({
  status: z.enum(["ok", "degraded"]),
  checks: z.object({
    process: z.literal("ok"),
    database: z.enum(["ok", "fail"]),
    storage: z.enum(["ok", "fail"]),
  }),
  worker: z.object({ lastHeartbeatAt: z.string().nullable(), lagSeconds: z.number().nullable() }),
  version: z.string(),
});

/* -------------------------------- employees ------------------------------ */

/** Fields every authenticated viewer may see about anyone (employment-only). */
export const EmploymentFieldsSchema = z.object({
  id: zUuid,
  code: zEmployeeCode,
  name: z.string(),
  role: z.string(),
  level: z.enum(LEVELS),
  levelLabel: z.string(),
  department: z.string(),
  workEmail: z.string(),
  city: z.string(),
  status: z.enum(EMPLOYEE_STATUSES),
  type: z.enum(EMPLOYMENT_TYPES),
  doj: zDate,
  tenureYears: z.number(),
  managerId: zUuid.nullable(),
  managerName: z.string().nullable(),
  approverId: zUuid.nullable(),
  approverName: z.string().nullable(),
  approverRole: z.string().nullable(),
  exitDate: zDate.nullable(),
  exitReason: z.string().nullable(),
  /**
   * Where this person's profile photo is served from, or null if they have not
   * set one and the generated identicon stands.
   *
   * An address rather than a boolean, and minted by the server rather than
   * assembled by the client, because it carries a `?v=` stamped from
   * `photo_updated_at`: the bytes are cacheable precisely because replacing them
   * changes the URL. It sits in the employment block — the one every authenticated
   * viewer receives — because a face is what a colleague reads a name next to, and
   * it is exactly as visible as the name itself.
   *
   * Optional rather than required, which is the exception this file makes for a
   * field nothing depends on: a payload minted without it — an older API during a
   * rolling deploy, a fixture written before photos existed — must render a
   * directory of identicons rather than fail the parse and blank the screen. A
   * missing *name* is a bug worth shouting about; a missing face already has an
   * answer, and every reader of this field treats absent and null alike.
   */
  photoUrl: z.string().nullable().optional(),
});

/** Personal block — only present when the viewer may see the full record. */
export const PersonalFieldsSchema = z.object({
  dob: zDate.nullable(),
  bloodGroup: z.string().nullable(),
  personalEmail: z.string().nullable(),
  phone: z.string().nullable(),
  currentAddress: z.string().nullable(),
  permanentAddress: z.string().nullable(),
  emergencyName: z.string().nullable(),
  emergencyRelation: z.string().nullable(),
  emergencyPhone: z.string().nullable(),
  /**
   * Who referred this person to the company — free text, since a referrer is
   * often outside it. Optional so a payload from before the field (an older API
   * mid-deploy, an old fixture) still parses; absent and null read the same.
   */
  referredBy: z.string().nullable().optional(),
});

export const EmployeeDetailSchema = EmploymentFieldsSchema.extend({
  full: z.boolean(),
  personal: PersonalFieldsSchema.nullable(),
  accessRole: z.enum(ACCESS_ROLES),
  /**
   * On the leadership team, whatever `department` says. Defaults so a payload from
   * before the field (older API mid-deploy, an old fixture) still parses.
   */
  isLeadership: z.boolean().default(false),
  /**
   * The leave approver HR assigned explicitly, by code — or null when leave follows
   * the reporting line. `approverName` is who leave actually routes to either way;
   * this only says whether that is an override. An assigned approver who has since
   * exited reads as null, because the resolver ignores them too. Defaults so an
   * older payload still parses.
   */
  leaveApproverCode: zEmployeeCode.nullable().default(null),
  tabs: z.array(z.string()),
});
export type EmployeeDetail = z.infer<typeof EmployeeDetailSchema>;

export const DirectoryRowSchema = EmploymentFieldsSchema.extend({
  leaveAllocated: z.number(),
  leaveUsed: z.number(),
  pendingCount: z.number(),
  pendingDays: z.number(),
  docsUploaded: z.number(),
  docsTotal: z.number(),
});
export type DirectoryRow = z.infer<typeof DirectoryRowSchema>;

export const EMPLOYEE_SORT_KEYS = [
  "name", "role", "department", "managerName", "leaveUsed", "pendingDays", "docsUploaded", "status", "code", "doj",
] as const;

export const ListEmployeesQuery = z.object({
  q: z.string().max(120).optional(),
  department: zDepartmentName.optional(),
  city: z.string().max(80).optional(),
  status: z.enum([...EMPLOYEE_STATUSES, "Active only", "All statuses"]).optional(),
  type: z.enum(EMPLOYMENT_TYPES).optional(),
  sort: z.enum(EMPLOYEE_SORT_KEYS).default("name"),
  dir: z.enum(["asc", "desc"]).default("asc"),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});

export const ListEmployeesResponse = z.object({
  rows: z.array(DirectoryRowSchema),
  total: z.number(),
  cities: z.array(z.string()),
  docsTotal: z.number(),
});

export const CreateEmployeeRequest = z.object({
  name: z.string().trim().min(1, "Add a name and designation to continue."),
  role: z.string().trim().min(1, "Add a name and designation to continue."),
  department: zDepartmentName,
  level: z.enum(LEVELS),
  managerCode: zEmployeeCode.nullable(),
  city: z.string().trim().max(80).default(""),
  type: z.enum(EMPLOYMENT_TYPES),
  doj: zDate,
  workEmail: z.string().email().optional(),
  personalEmail: z.string().email().optional(),
  phone: z.string().max(32).optional(),
  /**
   * `zRealDate`, not `zDate`: this one is written straight into a `date` column, so
   * the 30th of February has to fail here rather than as a Postgres sentence on a
   * form field. How far back and forward a *birthday* may sit is the server's call
   * (`assertBirthDateIsPlausible`), for the same reason `doj`'s window is — the
   * bound is relative to today, and a schema cannot read the clock.
   */
  dob: zRealDate.optional(),
  bloodGroup: z.string().max(8).optional(),
  currentAddress: z.string().max(400).optional(),
  permanentAddress: z.string().max(400).optional(),
  emergencyName: z.string().max(120).optional(),
  emergencyRelation: z.string().max(60).optional(),
  emergencyPhone: z.string().max(32).optional(),
});

/**
 * A contact number, or the empty string that clears one.
 *
 * `isContactNumber` accepts an Indian mobile or full E.164 and nothing else. The
 * field was `z.string().max(32)`, which accepted `"not-a-phone!!!"` with a 200 —
 * and an emergency number that cannot be dialled is the one field in this form
 * where being wrong is worse than being blank.
 */
const zContactNumber = z
  .string()
  .max(32, "That is too long for a phone number.")
  .refine(
    (value) => value.trim() === "" || isContactNumber(value),
    "Enter a 10-digit Indian mobile, or an international number starting with +.",
  );

/** One of a closed list, or empty. `""` is how the form clears the field. */
function zOptionalChoice(choices: readonly string[], sentence: string) {
  return z.string().refine((value) => value === "" || choices.includes(value), sentence);
}

/**
 * A real calendar date, or the empty string that clears one.
 *
 * The same bargain every other field on this form strikes: `""` means "remove
 * this", because a personal detail you cannot withdraw is not one you consented to
 * store. `zRealDate` cannot express it on its own — its refinement never sees `""`,
 * the shape check rejects it first — so the two are composed rather than a second
 * date rule being written out beside the first.
 */
const zOptionalRealDate = z
  .string()
  .refine(
    (value) => value === "" || zRealDate.safeParse(value).success,
    "Enter a real date, or leave it blank.",
  );

/**
 * The self-service fields whose change is announced to HR and the person's manager.
 *
 * `SENSITIVE_FIELDS` (domain.ts) is the prototype's list and stays exactly as it is
 * — it is the *product's* statement of what counts as protected, and it names
 * `bank`, a field this build has no editor for. This is that list as the self-service
 * form actually uses it, plus `dob`.
 *
 * A date of birth belongs on it. It is not a preference that moves with a person's
 * life the way a city or a phone number does; it is a fixed fact that has already
 * been filed elsewhere — PF, ESIC, insurance and gratuity all key off it — so an
 * edit after hire is either a typo being corrected, which HR has to know about
 * because the filed copy now disagrees with the record, or it is somebody shifting
 * an age-dependent entitlement, which is precisely the class of change `address` and
 * `bank` are on this list to catch. It is also the only field on this form with a
 * company-wide effect: the dashboard announces birthdays from it. And the practical
 * consequence of leaving it off is worse than the noise of putting it on — a
 * non-announced self-update writes no audit row at all, so the one field on the form
 * that feeds statutory records would be the one editable without a trace.
 *
 * Column names, because that is what the server compares a change against.
 * `current_address` and `permanent_address` replace the prototype's single
 * `address` column (migration 0030); both stay protected for the same reason.
 */
export const PROTECTED_SELF_FIELDS = [
  ...SENSITIVE_FIELDS,
  "dob",
  "current_address",
  "permanent_address",
] as const;

/**
 * The ten self-service fields, each bounded by what it actually means.
 *
 * Every field accepts `""` and treats it as "clear this". `personalEmail` used to
 * be `z.string().email()`, which made a personal address **unremovable**: `""`
 * failed `.email()`, the schema took no `null`, and once somebody had entered an
 * address there was no body that would delete it again. A contact detail you
 * cannot withdraw is not a detail you consented to store.
 *
 * The one rule that is *not* here is "an emergency contact needs a name". It
 * cannot be: this is a PATCH, so the body carrying a relationship need not also
 * carry the name already on the record, and a schema that only sees the body
 * would reject a legitimate edit or wave through `{ emergencyRelation: "Spouse" }`
 * on a nameless contact depending on which half it guessed. `updateSelf` enforces
 * it against the merged row, where the whole contact is visible.
 */
export const UpdateSelfRequest = z.object({
  city: z.string().max(80).optional(),
  phone: zContactNumber.optional(),
  personalEmail: z
    .string()
    .max(200)
    .refine(
      (value) => value === "" || z.string().email().safeParse(value).success,
      "Enter a personal email address like name@example.com, or leave it blank.",
    )
    .optional(),
  currentAddress: z.string().max(400).optional(),
  permanentAddress: z.string().max(400).optional(),
  emergencyName: z.string().max(120).optional(),
  emergencyRelation: zOptionalChoice(
    EMERGENCY_RELATIONSHIPS,
    `Choose one of ${EMERGENCY_RELATIONSHIPS.join(", ")}, or leave it blank.`,
  ).optional(),
  emergencyPhone: zContactNumber.optional(),
  bloodGroup: zOptionalChoice(
    BLOOD_GROUPS,
    `Choose one of ${BLOOD_GROUPS.join(", ")}, or leave it blank.`,
  ).optional(),
  /**
   * Date of birth — personal data its owner should be the one to enter.
   *
   * It was collected nowhere: not on "Add teammate", not here, and HR has no editor
   * for it either, so the row read "—" for the whole roster and the dashboard's
   * birthday celebration could never once fire. It sits on this form rather than on
   * an HR-only one because it is the person's own fact, alongside the blood group
   * and the emergency contact it will be read next to — and because every other
   * field HR owns is *employment* (band, department, reporting line, joining date),
   * which is the line `HR_MAINTAINED` draws on the screen.
   *
   * Plausibility — not in the future, not an absurd age — is enforced server-side
   * where the clock is; see `assertBirthDateIsPlausible`.
   */
  dob: zOptionalRealDate.optional(),
  /** Who referred them. Not protected: changing it notifies nobody. `""` clears it. */
  referredBy: z.string().trim().max(120, "Keep it under 120 characters.").optional(),
});

/**
 * What setting or clearing your own photo answers with.
 *
 * The new address, or null after a removal — so the caller re-renders from the
 * response instead of guessing a URL and racing the write that produced it. The
 * photo itself travels as multipart and has no request schema here: bytes are
 * validated by their content on the server (`validateUpload`), which is a check
 * no zod schema can make.
 */
export const ProfilePhotoResponse = z.object({ photoUrl: z.string().nullable() });
export type ProfilePhoto = z.infer<typeof ProfilePhotoResponse>;

/* ------------------------------ bank details ------------------------------ */

/** Indian Financial System Code: four letters, a zero, six letters or digits. */
export const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
/** Indian bank account numbers run 9–18 digits. */
export const ACCOUNT_NUMBER_PATTERN = /^[0-9]{9,18}$/;

/**
 * The salary account, as the person, HR Admin and Super Admin see it — and
 * nobody else (not a manager). `details` is null until somebody adds them.
 */
export const BankDetailsSchema = z.object({
  accountHolderName: z.string(),
  bankName: z.string(),
  accountNumber: z.string(),
  ifsc: z.string(),
  branch: z.string(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type BankDetails = z.infer<typeof BankDetailsSchema>;

export const BankDetailsResponse = z.object({
  details: BankDetailsSchema.nullable(),
  /** Always true for anyone the endpoint answers; here so the UI need not guess. */
  canEdit: z.boolean(),
});
export type BankDetailsResult = z.infer<typeof BankDetailsResponse>;

/**
 * A full replace, not a patch: an account is one fact, and half of one account
 * spliced onto half of another is exactly the mistake a salary run cannot absorb.
 * Spaces are stripped from the number and the IFSC is upper-cased before the
 * patterns are checked, so a number copied from a passbook with spaces still saves.
 */
export const UpdateBankDetailsRequest = z.object({
  accountHolderName: z.string().trim().min(1, "Enter the name on the account.").max(120, "Keep it under 120 characters."),
  bankName: z.string().trim().min(1, "Enter the bank's name.").max(120, "Keep it under 120 characters."),
  accountNumber: z
    .string()
    .transform((value) => value.replace(/[\s-]/g, ""))
    .pipe(z.string().regex(ACCOUNT_NUMBER_PATTERN, "An account number is 9 to 18 digits.")),
  ifsc: z
    .string()
    .transform((value) => value.trim().toUpperCase())
    .pipe(z.string().regex(IFSC_PATTERN, "An IFSC is 11 characters, like HDFC0001234.")),
  branch: z.string().trim().max(120, "Keep it under 120 characters.").default(""),
});
export type UpdateBankDetailsInput = z.input<typeof UpdateBankDetailsRequest>;

export const DesignationRequest = z.object({
  role: z.string().trim().min(1),
  level: z.enum(LEVELS),
  effectiveDate: zDate,
  note: z.string().trim().min(1),
});

/**
 * A Super Admin correcting the structural facts of an employment record.
 *
 * This is the editor the drawer never had: until now a person's department could
 * only be set at hire, and their reporting line could only be changed as a
 * side-effect of somebody above them exiting — there was no route in the API to say
 * "this person now sits here, and reports to them". Every field is a PATCH-style
 * optional: a body carries only what changed, and an absent key leaves the column
 * untouched.
 *
 * `managerCode` is `.nullable()` as well as `.optional()` because "reports to
 * nobody" is a real, distinct choice from "leave the manager as it is": `null`
 * clears the reporting line, an absent key preserves it. The server rejects a
 * manager that would close a cycle — a person cannot report to themselves or to
 * anyone already beneath them — because `manager_id` has no such constraint and a
 * loop hangs every chain walk in `authz`.
 *
 * Band and title are deliberately absent: they move through `DesignationRequest`,
 * which appends to employment history rather than overwriting the current row, and
 * duplicating them here would let a correction bypass that trail.
 */
export const UpdateEmploymentRequest = z.object({
  department: zDepartmentName.optional(),
  managerCode: zEmployeeCode.nullable().optional(),
  workEmail: z.string().email("Enter a valid work email address.").optional(),
  city: z.string().max(80).optional(),
  /** Leadership membership, independent of `department`. */
  isLeadership: z.boolean().optional(),
});

/**
 * `PUT /employees/:id/leave-approver` — HR Admin or Super Admin choosing who
 * approves this person's leave, independent of who they report to. `null` drops
 * the override so leave follows the reporting line again. The org chart does not
 * move. Requests already pending keep the approver stamped at submission.
 */
export const UpdateLeaveApproverRequest = z.object({
  approverCode: zEmployeeCode.nullable(),
});

/**
 * `PATCH /employees/:id/record` — HR Admin and Super Admin correcting the facts set
 * at hire: name, joining date, employment type, and confirmation (Probation ↔
 * Active). PATCH-style: an absent key leaves the column alone.
 *
 * `status` is the confirmation state of a full-timer only. An intern's status is
 * "Intern" by definition, and "Exited" is reached through the exit flow.
 */
export const RECORD_STATUSES = ["Probation", "Active"] as const;
export const UpdateRecordRequest = z.object({
  name: z.string().trim().min(1, "Enter a name.").max(120).optional(),
  doj: zDate.optional(),
  type: z.enum(EMPLOYMENT_TYPES).optional(),
  status: z.enum(RECORD_STATUSES).optional(),
});

export const ExitRequest = z.object({
  lastWorkingDate: zDate,
  reason: z.string().trim().min(1),
  notes: z.string().max(500).optional(),
});

export const HistoryEntrySchema = z.object({
  id: zUuid,
  effectiveDate: zDate,
  role: z.string(),
  level: z.enum(LEVELS),
  levelLabel: z.string(),
  note: z.string(),
  recordedBy: z.string(),
  recordedAtIst: z.string().nullable(),
});

export const OrgNodeSchema = z.object({
  id: zUuid,
  code: zEmployeeCode,
  name: z.string(),
  role: z.string(),
  department: z.string(),
  level: z.enum(LEVELS),
  status: z.enum(EMPLOYEE_STATUSES),
  managerId: zUuid.nullable(),
  /** On the leadership team — counted under "Leadership" alongside `department`. */
  isLeadership: z.boolean().default(false),
  /** Same address the directory carries; the canvas draws the biggest avatars. */
  photoUrl: z.string().nullable().optional(),
});

/**
 * The reporting tree. `nodes` excludes leavers and service accounts, and
 * `departmentCounts` is counted from those same nodes — so the sidebar's figures
 * and the cards on the canvas are the same population by construction.
 *
 * `baseHeadcount` is gone. It served `org_settings.base_headcount` — an
 * admin-typed company size — as a second, larger figure beside a chart drawn from
 * the roster, and the screen stopped reading it once the "All" row started
 * counting the cards it draws.
 *
 * **Reachability is a fact of the payload, not a client inference.** A tree built
 * from one root drops everyone the root cannot reach: somebody whose manager has
 * exited, somebody whose manager was cleared, anybody caught in a reporting loop.
 * They vanish from the canvas *and* from the counts beside it, with nothing to say
 * they were ever there. So the server states the answer: `rootIds` names every card
 * that heads a tree, chosen so that following `managerId` down from them touches
 * every node exactly once, and `unassignedIds` is the subset of those with no
 * reporting line at all — the CEO is a root but is not one of them, because having
 * no manager is the shape of the company rather than a gap in the record.
 *
 * **The figures reconcile.** `nodes.length` is what the chart draws;
 * `activeHeadcount` is every non-exited row in `employees`, service accounts
 * included, which is the number the dashboard's headcount tile shows. When they
 * differ, `omittedNote` is the sentence that says why, so a chart reading 65 beside
 * a dashboard reading 66 is explained on the screen instead of being an unexplained
 * discrepancy a reader has to discover.
 *
 * The five additions default rather than being required: a payload minted before
 * they existed still parses, and a client that ignores them renders exactly what it
 * rendered before.
 */
export const OrgChartResponse = z.object({
  nodes: z.array(OrgNodeSchema),
  departmentCounts: z.record(z.string(), z.number()),
  /** Every node that heads a tree, in draw order — the CEO first. Covers `nodes`. */
  rootIds: z.array(zUuid).default([]),
  /** The roots with nobody above them on the chart. A subset of `rootIds`. */
  unassignedIds: z.array(zUuid).default([]),
  /** The heading that group is drawn under, so the omission is visibly named. */
  unassignedLabel: z.string().default("No reporting line"),
  /** Every active employee, service accounts included — the company's headcount. */
  activeHeadcount: z.number().default(0),
  /** One finished sentence naming who is active but not drawn, or null. */
  omittedNote: z.string().nullable().default(null),
});

/* ------------------------------- departments ----------------------------- */

/**
 * One department, with the number of people in it right now.
 *
 * `count` is computed from `employees` at request time, never read from the
 * denormalised `departments.headcount` column — nothing recomputes that column on
 * hire, transfer or exit, which is how a breakdown summing to 64 came to sit under
 * a headcount card reading 66.
 *
 * `selectable` says whether a *new* teammate may be placed here. It is not a
 * visibility flag: a department that holds people is always listed, always
 * filterable and always counted, whether or not the "Add teammate" picker offers
 * it. Leadership is the case that matters — two people including the CEO.
 */
export const DepartmentSchema = z.object({
  id: zUuid,
  name: z.string(),
  count: z.number(),
  selectable: z.boolean(),
});
export type Department = z.infer<typeof DepartmentSchema>;

export const ListDepartmentsResponse = z.object({
  departments: z.array(DepartmentSchema),
  /** Sum of `count`, so an "All" row beside the breakdown adds up to it by construction. */
  activeHeadcount: z.number(),
});

/* ---------------------------------- leave -------------------------------- */

export const LeaveTypeSchema = z.object({
  id: zUuid,
  name: z.string(),
  /**
   * The quota in words — derived from the numbers below by `leaveQuotaLabel`, never
   * stored and never submitted. Served because every reader of this row wants the
   * sentence and none of them should have to build it the same way twice.
   */
  fteLabel: z.string(),
  internLabel: z.string(),
  /**
   * The quota, as a number this time.
   *
   * `fteLabel` has always been prose — "20 days / year" — and the 20 that actually
   * reached anybody's balance was a literal in a `switch` on the type's *name*, in
   * two files. The two could disagree and did not have to be told. These three are
   * the policy: what a full-timer gets, what an intern gets, and whether that
   * entitlement pro-rates over the months worked in the leave year. The labels
   * stay, because "Up to 1 month" and "As approved" say something no number can.
   */
  fteDays: z.number(),
  internDays: z.number(),
  proRata: z.boolean(),
  halfDayAllowed: z.boolean(),
  carryForward: z.string(),
  encashment: z.string(),
  paid: z.boolean(),
  colorVar: z.string(),
  sortOrder: z.number(),
});

/** A quota in days: halves are real (a half-day is bookable), 365 is the ceiling. */
const zLeaveDays = z.number().min(0).max(365).multipleOf(0.5);

const zLeaveLabel = z.string().trim().min(1).max(60);

/**
 * A new leave type.
 *
 * `name` appears here and deliberately nowhere in the patch: the name is not a
 * caption, it is the key several rules still match on — pro-rata used to, and the
 * medical-certificate threshold, the restricted-holiday special case and the
 * Teammates "leave used" column still do. Renaming a type in place would turn
 * those off silently, so a rename is not offered; everything else is.
 *
 * The two quota labels are absent for the opposite reason: they are not facts
 * anybody submits. They are read back off `fteDays` / `internDays` / `paid`, so
 * the sentence on the policy screen and the number on somebody's balance card
 * cannot drift apart the way they had.
 */
export const CreateLeaveTypeRequest = z.object({
  name: zLeaveLabel,
  fteDays: zLeaveDays,
  internDays: zLeaveDays,
  proRata: z.boolean(),
  halfDayAllowed: z.boolean(),
  carryForward: z.string().trim().max(60),
  encashment: z.string().trim().max(60),
  paid: z.boolean(),
  colorVar: z.string().trim().min(1).max(40),
});

/**
 * An edit. Partial, and refused when it changes nothing — an audit row claiming an
 * edit that never happened is worse than no row, the same reasoning
 * `UpdateHolidayRequest` is built on.
 */
export const UpdateLeaveTypeRequest = CreateLeaveTypeRequest.omit({ name: true })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: "Change something about the leave type.",
  });

/**
 * What a leave-type write answers with.
 *
 * `reallocated` is the count of leave-year balances the change rewrote. A quota is
 * only a real quota if it reaches the people it governs, so the write says how many
 * it reached rather than leaving the caller to assume.
 */
export const LeaveTypeWriteResponse = z.object({
  type: LeaveTypeSchema,
  reallocated: z.number(),
});

export const DeleteLeaveTypeResponse = z.object({ ok: z.literal(true) });

/**
 * One pool of one person's leave year.
 *
 * All four numbers travel, because `available` alone cannot be explained. The
 * ledger's invariant is `allocated >= booked + used`, and a card that shows only
 * what is left leaves the reader unable to see whether a missing day is booked, is
 * spent, or was never allocated. `paid` is here for the same reason: an unpaid
 * type opens at zero allocation by design, and a balance of 0 means something
 * different there than it does under Casual Leave.
 */
export const LeaveBalanceSchema = z.object({
  leaveTypeId: zUuid,
  name: z.string(),
  colorVar: z.string(),
  paid: z.boolean(),
  allocated: z.number(),
  available: z.number(),
  booked: z.number(),
  used: z.number(),
});

export const LeaveRequestSchema = z.object({
  id: zUuid,
  employeeId: zUuid,
  employeeCode: zEmployeeCode,
  employeeName: z.string(),
  department: z.string(),
  levelLabel: z.string(),
  leaveTypeId: zUuid,
  leaveType: z.string(),
  fromDate: zDate,
  toDate: zDate,
  days: z.number(),
  duration: z.enum(LEAVE_DURATIONS),
  reason: z.string(),
  status: z.enum(LEAVE_STATUSES),
  certificateFileName: z.string().nullable(),
  certificateDocumentId: zUuid.nullable(),
  approverId: zUuid.nullable(),
  approverName: z.string().nullable(),
  decidedAtIst: z.string().nullable(),
  decisionNote: z.string().nullable(),
  submittedAt: z.string(),
  /**
   * Why this request cannot be approved as it stands — empty when it can.
   *
   * Server-computed, one finished sentence per broken rule, in the same words the
   * 422 from `POST /leave/requests/:id/decision` carries. The queue was rendering
   * "Sabbatical cannot be a half day" in red beside a live Approve button, because
   * the row's rule violation was something the *client* worked out from
   * `halfDayAllowed` and the server never re-checked at decision time. Both halves
   * now come from one place: a non-empty array means Approve must be disabled, and
   * clicking it anyway is refused with these sentences.
   *
   * Only meaningful on a `pending` row — nothing offers Approve on a decided one —
   * but it is computed for every row because it is a fact about the request, not
   * about the screen. **Declining is never blocked**: a request that breaks its own
   * type's rules must always be refusable.
   *
   * Optional on the wire with a default of `[]` so a payload minted before the
   * field existed still parses; everything this API serves carries it.
   */
  approvalBlockers: z.array(z.string()).default([]),
});

export const ListLeaveRequestsQuery = z.object({
  status: z.enum([...LEAVE_STATUSES, "all"]).default("pending"),
  /** One request by id — the target of a leave alert. Combines with every other filter. */
  id: zUuid.optional(),
  scope: z.enum(["me", "team", "all"]).default("all"),
  department: zDepartmentName.optional(),
  leaveType: z.string().max(60).optional(),
  q: z.string().max(120).optional(),
  from: zDate.optional(),
  to: zDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});

export const CreateLeaveRequest = z.object({
  leaveTypeId: zUuid,
  fromDate: zDate,
  toDate: zDate,
  duration: z.enum(LEAVE_DURATIONS).default("Full day"),
  reason: z.string().trim().max(500).default(""),
  certificateDocumentId: zUuid.nullable().optional(),
  expectedDays: z.number().optional(),
});

export const LeaveDecisionRequest = z.object({
  decision: z.enum(["approved", "declined"]),
  note: z.string().max(500).optional(),
});

export const HolidaySchema = z.object({
  id: zUuid,
  date: zDate,
  name: z.string(),
  kind: z.enum(HOLIDAY_KINDS),
});

/* ---------------------------- holiday management -------------------------- */

/**
 * Publishing a holiday.
 *
 * The year is not a field: a holiday *is* a date, and `holidays.holiday_date`
 * carries the year with it. `zRealDate` is what makes "a four-digit year and a
 * real date" one check rather than two — the shape gives the four digits and the
 * refinement rejects the 30th of February.
 */
export const CreateHolidayRequest = z.object({
  date: zRealDate,
  name: z.string().trim().min(1, "Name the holiday.").max(80),
  kind: z.enum(HOLIDAY_KINDS),
});

/**
 * Editing one. Every field is optional — a typo in a name is the common case and
 * must not require restating the date — but a body that changes nothing at all is
 * refused rather than silently accepted, because it would otherwise write an
 * audit row claiming an edit that never happened.
 */
export const UpdateHolidayRequest = CreateHolidayRequest.partial().refine(
  (patch) => patch.date !== undefined || patch.name !== undefined || patch.kind !== undefined,
  { message: "Change the date, the name or the category." },
);

/**
 * Publishing a whole year's calendar in one go.
 *
 * A company announces twelve to fifteen holidays at once, and the one-at-a-time
 * form made that twelve to fifteen round trips — which is not merely tedious, it
 * is how a calendar ends up half published. Every row is a `CreateHolidayRequest`,
 * so a bulk add cannot accept a date or a name a single add would refuse.
 *
 * The 60 ceiling is a sanity bound, not a policy: no country has that many public
 * holidays, and the number exists so a runaway paste is refused by the schema
 * rather than by a transaction that has already locked the table.
 *
 * The duplicate check is about the *submitted list* only, and it has to live here
 * because the database cannot see it: two identical rows in one payload are one
 * insert and one unique-violation, and telling somebody "that is already on the
 * calendar" about a row they are adding in the same breath explains nothing. What
 * the list clashes with in the stored calendar is still the server's answer.
 */
export const CreateHolidayListRequest = z.object({
  holidays: z.array(CreateHolidayRequest).min(1, "Add at least one holiday.").max(60),
}).refine(
  (input) => {
    const seen = new Set<string>();
    for (const holiday of input.holidays) {
      const key = `${holiday.date}|${holiday.name.trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
    }
    return true;
  },
  { message: "The same holiday is listed twice — each date and name pair may appear once." },
);

/**
 * What a write answers with.
 *
 * `warning` is the honest half. Publishing a holiday onto a date people have
 * already been charged leave for does not break anything — their stored day counts
 * simply predate it — so the write is allowed and says so, rather than being
 * refused or going through in silence. Null when there is nothing to say.
 */
export const HolidayWriteResponse = z.object({
  holiday: HolidaySchema,
  warning: z.string().nullable(),
});

/**
 * What publishing a list answers with.
 *
 * `warnings` rather than one nullable sentence, and unlabelled: each is already a
 * whole statement naming its own date ("4 leave requests were counted before this
 * holiday existed, so they still charge 12 Nov 2026 as a working day"), and rows
 * that had nothing to say contribute nothing — so the array is shorter than
 * `holidays` and is not indexed against it.
 */
export const HolidayListWriteResponse = z.object({
  holidays: z.array(HolidaySchema),
  warnings: z.array(z.string()),
});

/** The year a holiday read is asking about. */
export const HolidayYearQuery = z.object({ year: zYear });

export const LeaveRoutingRowSchema = z.object({
  employeeId: zUuid,
  employeeCode: zEmployeeCode,
  employeeName: z.string(),
  levelLabel: z.string(),
  department: z.string(),
  approverName: z.string().nullable(),
});

export const BulkLeaveTemplate = z.enum(["Opening leave balances", "Historical leave records"]);

export const BulkLeavePreviewRequest = z.object({
  template: BulkLeaveTemplate,
  fileName: z.string().max(200),
  rows: z.array(z.record(z.string(), z.string())).max(5000),
});

export const BulkLeaveRowIssueSchema = z.object({
  row: z.number(),
  column: z.string(),
  message: z.string(),
  level: z.enum(["error", "warn"]),
});

export const BulkLeavePreviewResponse = z.object({
  token: z.string(),
  template: BulkLeaveTemplate,
  fileName: z.string(),
  totalRows: z.number(),
  validRows: z.number(),
  columns: z.array(z.object({ column: z.string(), matched: z.boolean() })),
  issues: z.array(BulkLeaveRowIssueSchema),
});

export const BulkLeaveCommitRequest = z.object({ token: z.string() });

export const BulkLeaveCommitResponse = z.object({
  imported: z.number(),
  skipped: z.number(),
  issues: z.array(BulkLeaveRowIssueSchema),
});

/* ------------------------------- attendance ------------------------------ */

/**
 * One resolved day of one person's month.
 *
 * `note` and `source` travel because `attendance_days` has carried both since
 * migration 0004 and `correctAttendanceDay` writes both — and until now neither
 * reached the client, so an HR correction ("regularised — VPN logs confirm")
 * looked identical to an imported row, on screen and in the exported CSV. A day
 * that costs somebody a day's pay has to be able to say who decided that and why.
 *
 * Both are null for a day resolved from the calendar rather than from a stored
 * row: weekends and public holidays are facts about the date, and a month nobody
 * has recorded yet still has them.
 */
export const AttendanceDaySchema = z.object({
  date: zDate,
  status: z.enum(ATTENDANCE_STATUSES),
  label: z.string(),
  note: z.string().nullable(),
  source: z.enum(ATTENDANCE_SOURCES).nullable(),
});

export const AttendanceRowSchema = z.object({
  employeeId: zUuid,
  employeeCode: zEmployeeCode,
  employeeName: z.string(),
  department: z.string(),
  days: z.array(AttendanceDaySchema),
});

export const AttendanceQuery = z.object({
  month: zMonth,
  department: zDepartmentName.optional(),
  q: z.string().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(10),
});

export const AttendanceResponse = z.object({
  month: zMonth,
  monthLabel: z.string(),
  rows: z.array(AttendanceRowSchema),
  totalMatching: z.number(),
  kpis: z.object({
    presentToday: z.number(),
    onLeave: z.number(),
    lop: z.number(),
    regularizationPending: z.number(),
    activeHeadcount: z.number(),
  }),
  presenceSeries: z.array(z.object({ d: z.number(), pct: z.number() })),
  holidays: z.array(z.object({ date: zDate, name: z.string() })),
});

export const MyAttendanceResponse = z.object({
  month: zMonth,
  monthLabel: z.string(),
  present: z.number(),
  leave: z.number(),
  lop: z.number(),
  holidays: z.number(),
  days: z.array(AttendanceDaySchema),
});

export const LopFeedResponse = z.object({
  month: zMonth,
  rows: z.array(z.object({
    employeeCode: zEmployeeCode,
    employeeName: z.string(),
    department: z.string(),
    lopDays: z.number(),
    dates: z.array(zDate),
  })),
});

/* --------------------------------- claims -------------------------------- */

export const ClaimLineSchema = z.object({
  id: zUuid,
  date: zDate,
  category: z.string(),
  description: z.string(),
  amount: zMoney,
});

export const ClaimSchema = z.object({
  id: zUuid,
  code: z.string(),
  employeeId: zUuid,
  employeeCode: zEmployeeCode,
  employeeName: z.string(),
  department: z.string(),
  month: z.string(),
  submittedOn: zDate,
  status: z.enum(CLAIM_STATES),
  statusLabel: z.string(),
  amount: zMoney,
  lines: z.array(ClaimLineSchema),
  approverId: zUuid.nullable(),
  approverName: z.string().nullable(),
  approvedOn: zDate.nullable(),
  paidOn: zDate.nullable(),
  note: z.string(),
  /**
   * The receipt, if there is one. `attachmentName` is empty exactly when
   * `hasAttachment` is false: the server derives both from the stored object, so a
   * claim can no longer advertise a filename for a file that was never uploaded.
   * Declared here because the client parses responses with this schema and zod
   * strips what the schema does not name.
   */
  attachmentName: z.string(),
  hasAttachment: z.boolean(),
});

export const CLAIM_SORT_KEYS = ["code", "employeeName", "department", "month", "amount", "status", "submittedOn"] as const;

export const ListClaimsQuery = z.object({
  scope: z.enum(["me", "team", "all"]).default("all"),
  status: z.enum([...CLAIM_STATES, "all"]).default("all"),
  department: zDepartmentName.optional(),
  range: z.enum(["This month", "Last month", "Quarter", "YTD"]).default("YTD"),
  q: z.string().max(120).optional(),
  sort: z.enum(CLAIM_SORT_KEYS).default("submittedOn"),
  dir: z.enum(["asc", "desc"]).default("asc"),
});

export const ListClaimsResponse = z.object({
  rows: z.array(ClaimSchema),
  /** Sums over the range- and scope-filtered set *before* the status filter. */
  kpis: z.object({
    submitted: zMoney,
    submittedCount: z.number(),
    managerApproved: zMoney,
    managerApprovedCount: z.number(),
    paid: zMoney,
    paidCount: z.number(),
    totalCount: z.number(),
  }),
});

export const CreateClaimRequest = z.object({
  month: z.string().min(1),
  lines: z.array(z.object({
    date: zDate,
    category: z.string().min(1),
    description: z.string().trim().min(1),
    amount: zMoney.refine((v) => Number(v) > 0, "amount must be positive"),
  })).min(1),
});

export const ClaimTransitionRequest = z.object({
  to: z.enum(CLAIM_STATES),
  note: z.string().max(500).optional(),
});

export const ClaimStatsResponse = z.object({
  months: z.array(z.object({ month: z.string(), label: z.string(), claimed: zMoney, paid: zMoney })),
});

/* --------------------------------- reviews ------------------------------- */

/**
 * How long a performance note may be.
 *
 * A note is a paragraph about a conversation, not an essay: BambooHR-class
 * products cap the field in the low thousands and every note in this database is
 * under 200 characters. The bound exists because the field was unbounded — a
 * 200 000-character note was accepted, stored and then rendered as one unbroken
 * line straight off the side of the drawer.
 */
export const REVIEW_NOTE_MAX = 4000;

const zReviewNote = z.string().trim().min(1).max(
  REVIEW_NOTE_MAX,
  `Keep the note under ${REVIEW_NOTE_MAX.toLocaleString("en-IN")} characters.`,
);

export const ReviewInstanceSchema = z.object({
  id: zUuid,
  employeeId: zUuid,
  employeeCode: zEmployeeCode,
  employeeName: z.string(),
  employeeStatusLabel: z.string(),
  department: z.string(),
  kind: z.string(),
  dueDate: zDate,
  state: z.enum(["Overdue", "Due soon", "Upcoming"]),
  ownerId: zUuid.nullable(),
  ownerName: z.string().nullable(),
  logged: z.boolean(),
  /** Present only when the viewer is permitted to see the note behind the log. */
  rating: z.number().min(1).max(5).nullable(),
  canLog: z.boolean(),
  /**
   * Whether *this* viewer may correct or withdraw the note behind this row, on
   * the same rules `amendReview` and `withdrawReview` enforce: the author within
   * the correction window may do either, and an HR admin may withdraw at any
   * time.
   *
   * They are here because the client had no honest way to work them out. It
   * inferred authorship from `rating != null` — which is the *note-visibility*
   * rule, not the authorship one — so a view-all principal who had logged the
   * review themselves was never offered Correct on their own note, and every
   * other view-all principal was offered it on somebody else's and got a 403. The
   * correction window is a server-side number the client cannot see at all.
   */
  canAmendLog: z.boolean(),
  canWithdrawLog: z.boolean(),
});

export const ListReviewsQuery = z.object({
  scope: z.enum(["me", "team", "all"]).default("all"),
  /**
   * The row filter. The three calendar buckets mean *outstanding* — a review
   * that has been logged has left the queue and answers `Logged` instead, which
   * is the only way "Overdue: 43" can ever go down as an admin works the list.
   */
  state: z.enum(["Overdue", "Due soon", "Upcoming", "Logged", "All states"]).default("All states"),
  department: zDepartmentName.optional(),
  q: z.string().max(120).optional(),
});

export const ListReviewsResponse = z.object({
  rows: z.array(ReviewInstanceSchema),
  /**
   * The tiles. `overdue`/`dueSoon`/`upcoming` count outstanding reviews only and
   * `logged` counts the rest, so the four always sum to the unfiltered row count.
   */
  counts: z.object({
    overdue: z.number(),
    dueSoon: z.number(),
    upcoming: z.number(),
    logged: z.number(),
    notesVisible: z.number(),
  }),
});

export const LogReviewRequest = z.object({
  rating: z.number().int().min(1).max(5),
  note: zReviewNote,
});

/**
 * Correcting a logged review.
 *
 * Same shape as logging it, because a correction *is* the review as the author
 * now says it happened. The previous rating and text are not discarded — they go
 * into the audit trail — so the record grows rather than being rewritten.
 */
export const AmendReviewRequest = LogReviewRequest;

export const ReviewNoteSchema = z.object({
  id: zUuid,
  kind: z.string(),
  rating: z.number().min(1).max(5),
  note: z.string(),
  authorId: zUuid,
  authorName: z.string(),
  date: zDate,
  timeIst: z.string().nullable(),
});

export const ListNotesResponse = z.object({
  notes: z.array(ReviewNoteSchema),
  canAdd: z.boolean(),
  /** Exact sentence the drawer prints under "Review notes". */
  visibilityCaption: z.string(),
});

export const AddNoteRequest = z.object({
  rating: z.number().int().min(1).max(5),
  note: zReviewNote,
  kind: z.string().default("Spontaneous note"),
  dueDate: zDate.optional(),
});

export const MyReviewsResponse = z.object({
  ownerName: z.string().nullable(),
  rows: z.array(z.object({
    id: zUuid,
    kind: z.string(),
    dueDate: zDate,
    state: z.enum(["Overdue", "Due soon", "Upcoming"]),
    ownerName: z.string().nullable(),
    /**
     * Whether the conversation has happened. The subject still never sees the
     * rating or the note — but "your manager ran this on 17 Aug" is their own
     * record, and without it the screen accuses them of being overdue forever.
     */
    logged: z.boolean(),
    loggedOn: zDate.nullable(),
  })),
});

/* -------------------------------- documents ------------------------------ */

export const DocumentTypeSchema = z.object({
  id: zUuid,
  name: z.string(),
  sortOrder: z.number(),
});

/**
 * One row of the KYC checklist — one active document type, uploaded or not.
 *
 * `hasFile` and `required` are the two facts the client could not previously see
 * and could not derive.
 *
 * A row can have a `documentId`, a filename, an uploader and a size and still
 * have no bytes behind it: the seeded rows carry a fabricated `size_bytes` over a
 * NULL `storage_key`, which is exactly the shape a half-migrated import leaves.
 * The drawer, having nothing better to go on, offered a preview, and the iframe
 * rendered the API's 404 JSON inside the document frame. `hasFile` is
 * `storage_key is not null` and is the only thing that should ever gate an
 * "Open" control.
 *
 * `required` is what makes "3 documents missing" mean something. Without it every
 * type counted equally, so a missing passport photo read as urgently as a missing
 * PAN card.
 */
export const EmployeeDocumentSchema = z.object({
  documentTypeId: zUuid,
  documentTypeName: z.string(),
  documentId: zUuid.nullable(),
  fileName: z.string().nullable(),
  uploadedOn: zDate.nullable(),
  uploadedByName: z.string().nullable(),
  uploadedAtIst: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  mimeType: z.string().nullable(),
  /** Whether bytes exist to open. Never inferred from `fileName` or `sizeBytes`. */
  hasFile: z.boolean(),
  /** Whether HR requires this proof; drives the "missing required" count. */
  required: z.boolean(),
});

export const ListDocumentsResponse = z.object({
  rows: z.array(EmployeeDocumentSchema),
  uploaded: z.number(),
  total: z.number(),
  canEdit: z.boolean(),
});

export const SignedFileResponse = z.object({ url: z.string(), expiresAt: z.string() });

/* ---------------------- dashboard, reports, resources -------------------- */

export const DashboardSummaryResponse = z.object({
  greetingName: z.string(),
  monthLabel: z.string(),
  kpis: z.object({
    onLeaveToday: z.number(),
    onLeaveTodayNote: z.string(),
    pendingApprovals: z.number(),
    reviewsDue: z.number(),
  }),
  /**
   * The company's size — counted from `employees`, and the only headcount this
   * response carries. `kpis.headcount` used to sit beside it holding
   * `org_settings.base_headcount - exits`, an admin-typed figure no hire ever
   * updated; the two read 65 and 67 on the same screen until the Dashboard tile
   * moved here, after which nothing read it at all.
   */
  activeEmployees: z.number(),
  whosOutToday: z.array(z.object({
    employeeId: zUuid, employeeCode: zEmployeeCode, name: z.string(), leaveType: z.string(), half: z.boolean(),
  })),
  events: z.array(z.object({
    employeeId: zUuid, employeeCode: zEmployeeCode, name: z.string(), department: z.string(),
    kind: z.enum(["Birthday", "Work anniversary", "New joinee"]), inDays: z.number(), when: z.string(), date: zDate, years: z.number().nullable(),
  })),
  departments: z.array(z.object({ name: z.string(), count: z.number() })),
});

/**
 * Who is actually at work today, counted live rather than read from a snapshot.
 *
 * Presence is a *presumption*, not a record: everyone on the active roster is in
 * unless something says otherwise, and the only things that say otherwise are an
 * approved absence and an HR correction. A leave request still under review says
 * nothing — the employee is at work until an approver decides, whatever they may
 * have arranged in a chat — and a declined one says the same. Both are therefore
 * counted present, and `pendingLeave` exists to say so out loud rather than leave
 * the reader wondering whether the tile has noticed them.
 *
 * `halfDay` is counted present for the same reason: half a day at work is a day
 * with the person in it. It is reported separately because the attendance
 * register cannot hold a half day (`writeLeaveAttendance`) and this is where that
 * detail survives.
 *
 * `nonWorkingReason` is set on a weekend or a public holiday, when `present` is
 * zero because nobody was expected in — not because nobody came.
 */
export const TodayHeadcountSchema = z.object({
  date: zDate,
  /** The non-exited roster: the number `present` is measured against. */
  active: z.number(),
  /** `active` minus approved full-day leave and recorded loss of pay. */
  present: z.number(),
  onLeave: z.number(),
  /** Approved half days — counted inside `present`. */
  halfDay: z.number(),
  lop: z.number(),
  /** Requests covering today with no decision yet — counted inside `present`. */
  pendingLeave: z.number(),
  nonWorkingReason: z.string().nullable(),
  note: z.string(),
});

/**
 * One working day of the recent past, counted the way `TodayHeadcountSchema` counts
 * today — the roster active on that date, less approved full-day leave and recorded
 * loss of pay.
 *
 * Weekends and public holidays are **absent from the series**, not plotted at zero.
 * A point that drops to the floor every Saturday describes the calendar rather than
 * attendance, which is the same reason `presenceSeries` drops them. So the window is
 * the last ten *working* days and can span a fortnight.
 *
 * `active` is per-day and date-aware — who had joined and had not yet left on that
 * date — because a chart of the past must not be rewritten by somebody's status
 * changing today. The monthly bars beside it are snapshots for the same reason. It
 * is also what the client crops the line's axis to: the roster is the ceiling a
 * day's headcount cannot exceed.
 */
export const DailyHeadcountSchema = z.object({
  date: zDate,
  /** `19 Aug` — the axis tick and the tooltip's title. */
  label: z.string(),
  active: z.number(),
  present: z.number(),
  onLeave: z.number(),
  /** Approved half days — counted inside `present`. */
  halfDay: z.number(),
  lop: z.number(),
});

export const HeadcountResponse = z.object({
  series: z.array(z.object({ month: z.string(), label: z.string(), value: z.number() })),
  daily: z.array(DailyHeadcountSchema),
  today: TodayHeadcountSchema,
});

export const ReportsSummaryResponse = z.object({
  rangeLabel: z.string(),
  kpis: z.object({
    avgTenureYears: z.number(),
    onLeaveThisMonth: z.number(),
    attritionTtmPct: z.number(),
    attritionDeltaPct: z.number(),
    avgLeavePerEmployee: z.number(),
  }),
  tenureBands: z.array(z.object({ band: z.string(), count: z.number() })),
  departments: z.array(z.object({ name: z.string(), count: z.number() })),
  longestTenure: z.object({ name: z.string(), years: z.number() }).nullable(),
  activeCount: z.number(),
});

export const AttritionResponse = z.object({
  series: z.array(z.object({ month: z.string(), value: z.number() })),
});

export const LeaveByMonthResponse = z.object({
  series: z.array(z.object({ month: z.string(), value: z.number() })),
});

export const ResourceSchema = z.object({
  id: zUuid,
  groupTitle: z.string(),
  groupIcon: z.string(),
  name: z.string(),
  kind: z.enum(["PDF", "Link", "Sheet", "Doc"]),
  meta: z.string(),
  url: z.string().nullable(),
  hasFile: z.boolean(),
  pages: z.number().nullable(),
});

export const ListResourcesResponse = z.object({
  groups: z.array(z.object({ title: z.string(), icon: z.string(), items: z.array(ResourceSchema) })),
  canManage: z.boolean(),
  canDownload: z.boolean(),
});

/**
 * `url` accepts an absolute link *or* an in-app path.
 *
 * `z.string().url()` refused `/org-chart`, and the seeded library already
 * contains exactly that kind of row — the resources that point at a screen of
 * this product rather than out at the web. An admin could see those resources and
 * could not add another one. Anything else is refused, and a scheme-relative
 * `//evil.example` is refused with it: it looks like a path and resolves to
 * another origin.
 */
const zResourceUrl = z.string().max(2000).refine(
  (value) =>
    (value.startsWith("/") && !value.startsWith("//")) ||
    z.string().url().safeParse(value).success,
  "Enter a full https:// link, or an in-app path beginning with /.",
);

export const CreateResourceRequest = z.object({
  groupTitle: z.string().min(1),
  name: z.string().min(1),
  kind: z.enum(["PDF", "Link", "Sheet", "Doc"]),
  meta: z.string().default(""),
  url: zResourceUrl.nullable().optional(),
});

/**
 * Editing a resource — every field optional but at least one change.
 *
 * `url` being nullable means an admin can clear a link target; `meta` is
 * optional because clearing a description is valid (the UI sends ""). A body
 * that changes nothing is refused so no audit-like noise is created, matching
 * `UpdateHolidayRequest`.
 */
export const UpdateResourceRequest = CreateResourceRequest.partial().refine(
  (patch) => Object.keys(patch).length > 0,
  { message: "Change something about the resource." },
);

export const CreateResourceGroupRequest = z.object({
  title: z.string().trim().min(1, "Name the group.").max(80),
  icon: z.string().trim().max(40).optional(),
});

export const UpdateResourceGroupRequest = z
  .object({
    title: z.string().trim().min(1, "Name the group.").max(80).optional(),
    icon: z.string().trim().max(40).optional(),
  })
  .refine((patch) => patch.title !== undefined || patch.icon !== undefined, {
    message: "Change the name or icon.",
  });

/* ------------------------------- onboarding ------------------------------ */

/**
 * One in-house tool a new joiner is pointed at, as `org_settings.botpresso_tools`
 * stores it.
 *
 * Declared once and served by both the onboarding checklist and the Settings
 * configuration screen, because they are the same rows and a rename has to land
 * in both.
 *
 * `url` is where that one tool opens — optional, and empty for a tool an operator
 * adds without one, in which case the card falls back to `toolsUrl` and behaves
 * as every card did before. It reuses `zResourceUrl` so an operator writing this
 * value is held to the rule the resource library already applies: a full link, or
 * an in-app path, and never a scheme-relative `//evil.example`.
 */
export const BotpressoToolSchema = z.object({
  name: z.string(),
  tag: z.string(),
  desc: z.string(),
  url: zResourceUrl.optional(),
});

export const OnboardingResponse = z.object({
  percent: z.number(),
  completed: z.number(),
  total: z.number(),
  phases: z.array(z.object({
    phase: z.string(),
    tasks: z.array(z.object({
      taskId: z.string(), label: z.string(), description: z.string(),
      icon: z.string(), link: z.string().nullable(), done: z.boolean(),
    })),
  })),
  /**
   * The tool stack card. Configuration, not code: these two keys have been in
   * `org_settings` since the first seed and were read by nothing, so the
   * Onboarding screen hard-coded the same eight tools from `@hrms/shared` and
   * renaming one meant a deploy.
   */
  tools: z.array(BotpressoToolSchema),
  toolsUrl: z.string(),
});

export const ToggleTaskRequest = z.object({ done: z.boolean() });

/* -------------------------------- settings ------------------------------- */

export const AccessSettingsResponse = z.object({
  matrix: z.record(z.string(), z.record(z.string(), z.string())),
  roles: z.array(z.string()),
  modules: z.array(z.string()),
  assignments: z.array(z.object({
    employeeId: zUuid, employeeCode: zEmployeeCode, name: z.string(),
    role: z.string(), department: z.string(), accessRole: z.enum(ACCESS_ROLES),
  })),
});

export const UpdateAccessRequest = z.object({
  assignments: z.array(z.object({ employeeId: zUuid, accessRole: z.enum(ACCESS_ROLES) })).min(1),
});

export const NotificationTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  trigger: z.string(),
  audience: z.string(),
  enabled: z.boolean(),
  subject: z.string(),
  body: z.string(),
});

export const NotificationSettingsResponse = z.object({ templates: z.array(NotificationTemplateSchema) });

export const UpdateNotificationSettingsRequest = z.object({
  templates: z.array(z.object({ id: z.string(), enabled: z.boolean() })).min(1),
});

export const ConfigurationResponse = z.object({
  company: z.object({ legal: z.string(), address: z.string() }),
  departments: z.array(z.string()),
  baseHeadcount: z.number(),
  leaveYear: z.string(),
  noteViewAll: z.array(zEmployeeCode),
  noteAddAll: z.array(zEmployeeCode),
  groups: z.array(z.object({ icon: z.string(), title: z.string(), items: z.array(z.string()) })),
  /**
   * How often each population is reviewed, in months. Read by the Reviews card,
   * which is the screen that sets them, and by the nightly cadence job, which is
   * what makes them mean anything.
   */
  reviewCadence: z.object({
    internMonths: z.number().int(),
    probationMonths: z.number().int(),
    fullTimeMonths: z.number().int(),
  }),
  /** The same two `org_settings` keys the onboarding checklist renders. */
  botpressoTools: z.array(BotpressoToolSchema),
  toolsUrl: z.string(),
});

const zCadenceMonths = z.number().int().min(1).max(12).optional();

export const UpdateConfigurationRequest = z.object({
  legalName: z.string().min(1).optional(),
  address: z.string().min(1).optional(),
  baseHeadcount: z.number().int().min(0).optional(),
  noteViewAll: z.array(zEmployeeCode).optional(),
  noteAddAll: z.array(zEmployeeCode).optional(),
  /**
   * A review every N months, per population. Bounded because the cadence walks
   * forward from a joining date one interval at a time: zero would not terminate
   * and a year is already the longest gap anyone would call a cadence.
   */
  internCadenceMonths: zCadenceMonths,
  probationCadenceMonths: zCadenceMonths,
  fullTimeCadenceMonths: zCadenceMonths,
});

/* --------------------------------- alerts -------------------------------- */

/**
 * One alert row. Every row is one item — one leave request, one review, one claim,
 * one celebration — never a count of several.
 *
 * `id` is unique per row and stable for as long as the item exists, so it is also
 * the key a dismissal is stored under. `kind` is what the client routes on; it is a
 * plain string so the server can add a kind without a matching client release (an
 * unknown kind renders as an unlinked row).
 */
export const AlertSchema = z.object({
  id: z.string(),
  kind: z.string(),
  icon: z.string(),
  colorVar: z.string(),
  text: z.string(),
  when: z.string(),
  /** The person the row is about, for destinations that take a search term. */
  subjectName: z.string().nullable(),
  /** The leave request or claim the row is about; null for the other kinds. */
  targetId: z.string().nullable(),
  /** Opening the row removes it for this viewer (review alerts). */
  dismissOnOpen: z.boolean(),
  /**
   * Days the item has been waiting: since a request or claim was submitted, since a
   * review fell due. Negative for a review not yet due (days until it is). The
   * full alerts page filters and sorts on it; `when` is its sentence form.
   */
  ageDays: z.number(),
});

export const ListAlertsResponse = z.object({ alerts: z.array(AlertSchema), count: z.number() });

/**
 * Review alerts are the only kind a click removes; every other kind clears itself
 * when the underlying item is decided. The pattern is the review id format
 * `listAlerts` emits, so nothing else can be written into the dismissal table.
 */
export const DISMISSIBLE_ALERT_ID = /^review-(?:overdue|due-week):[0-9a-f-]{36}:\d{4}-\d{2}-\d{2}:.{1,120}$/;

export const DismissAlertRequest = z.object({
  id: z.string().max(250).regex(DISMISSIBLE_ALERT_ID),
});

/* ---------------------------------- audit -------------------------------- */

export const AuditEntrySchema = z.object({
  id: zUuid,
  timeIst: z.string(),
  createdAt: z.string(),
  action: z.string(),
  label: z.string(),
  actorName: z.string(),
  statusLabel: z.string(),
  kind: z.enum(["approve", "decline", "add", "promote", "exit", "import"]),
  reversible: z.boolean(),
  undone: z.boolean(),
  entityType: z.string(),
  entityId: zUuid.nullable(),
});

export const ListAuditQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
  entityType: z.string().max(40).optional(),
  entityId: zUuid.optional(),
  actorId: zUuid.optional(),
  /**
   * `all` is the whole trail and is clamped to what the viewer may see; `mine`
   * is the viewer's own actions; `team` is everything the viewer's reporting
   * subtree did, which is what a manager's Recent actions tab wants.
   */
  scope: z.enum(["all", "mine", "team"]).default("all"),
});

export const ListAuditResponse = z.object({
  entries: z.array(AuditEntrySchema),
  nextCursor: z.string().nullable(),
});

export const UndoResponse = z.object({ ok: z.literal(true), undoneAuditId: zUuid, newAuditId: zUuid });

export type ApiRoutes = {
  "GET /api/v1/me": Me;
  "GET /api/v1/employees": z.infer<typeof ListEmployeesResponse>;
  "GET /api/v1/leave/requests": { rows: z.infer<typeof LeaveRequestSchema>[]; total: number };
};
